import {
    ChangeEvent,
    forwardRef,
    useContext,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';

import {
    Button,
    ChevronDownIcon,
    StirlingIcon,
    TextField,
} from '@selina-finance/ui';
import { Form, Formik } from 'formik';
import { useNavigate } from 'react-router-dom';

import { ApplicationContext } from '@src/app/contexts/Application';
import {
    formItems,
    purposeArray,
} from '@src/app/formModels/eligibility/loanDetails';
import {
    getDefaultValues,
    getFormHelpers,
    getValidationSchema,
} from '@src/shared/helpers/input';
import BackButton from '@src/ui/components/BackButton/BackButton';
import ErrorContainer from '@src/ui/components/ErrorContainer/ErrorContainer';
import { HttpError } from '@src/ui/components/HttpError';
import ProgressMenu from '@src/ui/components/Progress/ProgressMenu';
import SelectBox from '@src/ui/components/SelectBox/SelectBox';

interface LoanDetailsViewProps {
    loanDetailsData: any;
    saveLoanDetails: (data: any) => Promise<any>;
}

const defaultValues = getDefaultValues(formItems);
const validationSchema = getValidationSchema(formItems);

const LoanDetailsView = forwardRef((props: LoanDetailsViewProps, ref) => {
    const formikRef = useRef<any>(null);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const { setCurrentForm, setExternalApplicationId } =
        useContext(ApplicationContext);

    useEffect(() => {
        formikRef.current.values = {
            ...defaultValues,
            ...props.loanDetailsData,
        };
    });

    useEffect(() => {
        setCurrentForm(undefined);
    }, [setCurrentForm]);

    useEffect(() => {
        if (formikRef.current) {
            setCurrentForm(formikRef.current);
        }
    }, [formikRef, setCurrentForm]);

    const saveForLater = () => {
        const values = formikRef.current.values;

        props.saveLoanDetails(values).then((data: any) => {
            if (!data.success) {
                setError(data.error);
            }
        });
    };

    useImperativeHandle(ref, () => ({
        saveMethod: saveForLater,
    }));

    const url = new URL(window.location.href);

    const urlExternalApplicationId = url.searchParams.get(
        'externalApplicationId',
    );

    useEffect(() => {
        if (urlExternalApplicationId) {
            setExternalApplicationId(urlExternalApplicationId);
            localStorage.setItem(
                'externalApplicationId',
                urlExternalApplicationId,
            );
        }
    }, [setExternalApplicationId, urlExternalApplicationId]);

    const handleAmountChange =
        (formikProps: any, fieldName: string) =>
        (e: ChangeEvent<HTMLInputElement>) => {
            const numericValue = parseFloat(
                e.currentTarget.value.replace(/,/g, ''),
            );
            formikProps.setFieldValue(fieldName, numericValue);
        };

    const handleSecondPurposeChange =
        (formikProps: any, fieldName: string) =>
        (e: ChangeEvent<HTMLInputElement>) => {
            const purpose = e.currentTarget.value;
            if (purpose === 'None') {
                formikProps.setFieldValue(
                    'facilities',
                    formikProps.values.facilities.slice(0, 1),
                );
                return;
            }
            formikProps.setFieldValue(fieldName, purpose);
        };

    return (
        <div className='flex flex-row justify-between'>
            <div className='w-full sm:w-1/2 lg:w-2/5 mt-20 sm:mt-0 sm:mr-10'>
                <div className='hidden lg:block mb-2'>
                    <BackButton />
                </div>
                <p className='text-2xl lg:text-4xl font-sora-bold tracking-tighter'>
                    Your loan
                </p>
                <div className='relative mb-15'>
                    <Formik
                        enableReinitialize
                        initialValues={{
                            ...defaultValues,
                            ...props.loanDetailsData,
                        }}
                        validationSchema={validationSchema}
                        onSubmit={async (values) => {
                            setError(null);
                            props.saveLoanDetails(values).then((data: any) => {
                                const redirectTo =
                                    values.redirectTo ||
                                    '/eligibility/applicants';
                                if (data.success) {
                                    navigate(redirectTo);
                                }
                                if (!data.success) {
                                    setError(data.error);
                                }
                            });
                        }}
                        innerRef={(ref) => (formikRef.current = ref)}
                    >
                        {(formikProps) => {
                            const { getError } = getFormHelpers(
                                formItems,
                                formikProps,
                            );

                            const amountAssistiveText = 'From £5k to £500k';
                            const termAssistiveText = '5 - 30 years';

                            const primaryPurposeItems = purposeArray.filter(
                                (i) =>
                                    i.value !==
                                    formikProps.values.facilities?.[1]
                                        ?.allocationPurpose,
                            );

                            const secondaryPurposeItems = [
                                { label: 'None', value: 'None' },
                                ...purposeArray.filter(
                                    (i) =>
                                        i.value !==
                                        formikProps.values.facilities?.[0]
                                            ?.allocationPurpose,
                                ),
                            ];

                            return (
                                <Form>
                                    <div className='grid grid-col-1 mt-6 mb-6 gap-10'>
                                        <TextField
                                            type='tel'
                                            label='I want to borrow'
                                            prefix={<StirlingIcon />}
                                            maxLength={7}
                                            mask={'currency' as any}
                                            name='amount'
                                            assistiveText={
                                                !getError('amount')
                                                    ? amountAssistiveText
                                                    : ''
                                            }
                                            value={
                                                formikProps.values.amount?.toString() ||
                                                ''
                                            }
                                            onChange={handleAmountChange(
                                                formikProps,
                                                'amount',
                                            )}
                                            onBlur={formikProps.handleBlur}
                                            error={getError('amount')}
                                        />
                                        <TextField
                                            label='And pay it back over'
                                            suffix='Years'
                                            className='w-48'
                                            maxLength={2}
                                            mask='number'
                                            name='term'
                                            assistiveText={
                                                !getError('term')
                                                    ? termAssistiveText
                                                    : ''
                                            }
                                            value={
                                                formikProps.values.term?.toString() ||
                                                ''
                                            }
                                            onChange={(
                                                e: ChangeEvent<HTMLInputElement>,
                                            ) => formikProps.handleChange(e)}
                                            onBlur={formikProps.handleBlur}
                                            error={getError('term')}
                                        />
                                        <SelectBox
                                            items={primaryPurposeItems}
                                            suffix={<ChevronDownIcon />}
                                            label='Primary purpose'
                                            name='facilities[0].allocationPurpose'
                                            validState={
                                                !!formikProps.values
                                                    .facilities?.[0]
                                                    ?.allocationPurpose
                                            }
                                            error={getError(
                                                'facilities[0].allocationPurpose',
                                            )}
                                            value={
                                                formikProps.values
                                                    .facilities?.[0]
                                                    ?.allocationPurpose || ' '
                                            }
                                            onChange={(
                                                e: ChangeEvent<HTMLInputElement>,
                                            ) =>
                                                formikProps.setFieldValue(
                                                    'facilities[0].allocationPurpose',
                                                    e.currentTarget.value,
                                                )
                                            }
                                            onBlur={() =>
                                                formikProps.setFieldTouched(
                                                    'facilities[0].allocationPurpose',
                                                    true,
                                                )
                                            }
                                        />

                                        <TextField
                                            type='tel'
                                            label='Primary purpose amount'
                                            prefix='£'
                                            maxLength={7}
                                            mask={'currency' as any}
                                            name='facilities[0].allocationAmount'
                                            value={
                                                formikProps.values.facilities?.[0]?.allocationAmount?.toString() ||
                                                ''
                                            }
                                            onChange={handleAmountChange(
                                                formikProps,
                                                'facilities[0].allocationAmount',
                                            )}
                                            onBlur={formikProps.handleBlur}
                                            error={getError(
                                                'facilities[0].allocationAmount',
                                            )}
                                        />

                                        <SelectBox
                                            items={secondaryPurposeItems}
                                            suffix={<ChevronDownIcon />}
                                            label='Secondary purpose (optional)'
                                            name='facilities[1].allocationPurpose'
                                            validState={
                                                !!formikProps.values
                                                    .facilities?.[1]
                                                    ?.allocationPurpose
                                            }
                                            error={getError(
                                                'facilities[1].allocationPurpose',
                                            )}
                                            value={
                                                formikProps.values
                                                    .facilities?.[1]
                                                    ?.allocationPurpose || ' '
                                            }
                                            onChange={handleSecondPurposeChange(
                                                formikProps,
                                                'facilities[1].allocationPurpose',
                                            )}
                                            onBlur={() =>
                                                formikProps.setFieldTouched(
                                                    'facilities[1].allocationPurpose',
                                                    true,
                                                )
                                            }
                                        />
                                        {formikProps.values.facilities?.[1]
                                            ?.allocationPurpose && (
                                            <TextField
                                                type='tel'
                                                label='Secondary purpose amount'
                                                prefix='£'
                                                maxLength={7}
                                                mask={'currency' as any}
                                                name='facilities[1].allocationAmount'
                                                value={
                                                    formikProps.values.facilities?.[1]?.allocationAmount?.toString() ||
                                                    ''
                                                }
                                                onChange={handleAmountChange(
                                                    formikProps,
                                                    'facilities[1].allocationAmount',
                                                )}
                                                onBlur={formikProps.handleBlur}
                                                error={getError(
                                                    'facilities[1].allocationAmount',
                                                )}
                                            />
                                        )}
                                    </div>
                                    {error && <HttpError error={error} />}
                                    <ErrorContainer formikProps={formikProps} />
                                    <div className='flex gap-5 justify-end mt-12'>
                                        <Button
                                            color='secondary'
                                            type='submit'
                                            size={'large'}
                                            className='w-full'
                                        >
                                            Continue
                                        </Button>
                                    </div>
                                </Form>
                            );
                        }}
                    </Formik>
                </div>
            </div>
            <ProgressMenu />
        </div>
    );
});
export default LoanDetailsView;
